import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { ViewModel as EmptyData } from 'TypeGen/Order/Empty/view-model';
import { Command as EmptyRequest } from 'TypeGen/Order/Empty/command';
import { Loader } from '@progress/kendo-react-indicators';
import { JsonResponse } from 'TypeGen/json-response';
import { useEffect, useState } from 'react';
import { ApiLink } from 'TypeGen/api-link';
import { ApiCommand } from 'TypeGen/api-command';
import { fetchApi } from 'core/fetchApi';
import { parseJSON } from 'date-fns';
import { formatNumber } from '@progress/kendo-intl';
import { formatDateTime } from 'utils/format';
import { useTrack } from '../context';
import { Grid, Typography, Link, Box } from '@mui/material';
import { Input } from '@progress/kendo-react-inputs';

type Props = {
  Link: ApiLink;
  CloseDialog: () => void;
}

const Empty = (props: Props) => {

  const track = useTrack();
  const [data, setData] = useState<EmptyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dateTime, setDateTime] = useState<Date | null>(new Date());
  const [signedBy, setSignedBy] = useState('');

  const empty = () => {
    if (!dateTime) {
      alert('Please select a date/time');
      return;
    }
    if (!signedBy) {
      alert('Please enter a Signed By');
      return;
    }

    setSaving(true);
    const link = data!.Links.find(x => x.Name == "Empty") as ApiCommand;
    const request = {
        DateTime: dateTime,
        SignedBy: signedBy,
        UnhookTrailer: false,
        TrailerFreightOnBoard: false,
        NewTrailerNumber: ''
    } as EmptyRequest;
    fetchApi<JsonResponse>({ url: link.Link, payload: request, method: link.Method })
        .then((response) => {
            if (response.Success) {
                track?.refresh();
                props.CloseDialog();
            } else {
                alert(response.ErrorMessage);
            }
        })
        .catch((e) => {
            // If not problem details
            if (!e?.status) alert('Unable to empty');
        })
        .finally(() => setSaving(false));
  }

  useEffect(() => {
    fetchApi<EmptyData>({ url: props.Link.Link, payload: {}, method: props.Link.Method })
        .then((data) => {
            setData(data);
            setDateTime(parseJSON(data.DateTime));
            setSignedBy(data.SignedBy);
            setLoading(false);
        });
  }, [props.Link]);

  if (loading || !data) {
    return <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image"></div>
      <div className="k-loading-color"></div>
    </div>
  }

  return (
    <Dialog
      title={`Empty (${data.Sequence} of ${data.StopCount}) - Pro # ${data.OrderNumber}`}
      onClose={props.CloseDialog}
      className='dialog-w1140'
    >
      {saving && <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Loader type="converging-spinner" />
      </Box>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body1">
            {data.LocationName !== null && <>{data.LocationName.toUpperCase()}<br /></>}
            {data.AddressLine1 !== null && <>{data.AddressLine1.toUpperCase()}<br /></>}
            {data.City}, {data.State} {data.ZipCode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body1" gutterBottom>
            Scheduled {data.TypeName}{' '}
            <Link href="#" onClick={() => setDateTime(parseJSON(data.ScheduledDateTime))}>
              {formatDateTime(data.ScheduledDateTime)}
            </Link>
          </Typography>
          {data.ArrivalDateTime && (
            <Typography variant="body1" gutterBottom>
              Arrival{' '}
              <Link href="#" onClick={() => setDateTime(parseJSON(data.ArrivalDateTime!))}>
                {formatDateTime(data.ArrivalDateTime)}
              </Link>
            </Typography>
          )}
          {data.DepartDateTime && (
            <Typography variant="body1" gutterBottom>
              Departure{' '}
              <Link href="#" onClick={() => setDateTime(parseJSON(data.DepartDateTime!))}>
                {formatDateTime(data.DepartDateTime)}
              </Link>
            </Typography>
          )}
          {data.Miles > 0 && (
            <Typography variant="body1">
              {formatNumber(data.Miles, 'n0')} Stop Miles
            </Typography>
          )}
        </Grid>
      </Grid>

      <DateTimePicker
        required
        label='Empty Date/Time'
        format="MM/dd/yyyy HH:mm"
        formatPlaceholder="formatPattern"
        value={dateTime}
        onChange={(e) => setDateTime(e.value)}
        width="100%"
      />

      <Input
        required
        label='Signed By'
        value={signedBy}
        onChange={(e) => setSignedBy(e.value)}
        style={{ width: "100%" }}
      />

      <DialogActionsBar layout="end">
        <Button onClick={props.CloseDialog} disabled={saving}>Cancel</Button>
        <Button themeColor="primary" onClick={empty} disabled={saving}>Empty</Button>
      </DialogActionsBar>
    </Dialog>
  );
}

export default Empty;